import { useState, useEffect, useRef, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/use-auth';
import { confirmMfaSetup } from '@/lib/api/users';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Card, CardContent } from '@/components/ui';
import { Button, Input, Label, Spinner } from '@/components/ui';
import internetqLogo from '@/assets/internetq-logo.png';

/**
 * MFA-Ersteinrichtung (Spec 0045 Phase 4).
 * Zeigt QR-Code + manuellen Key, verifiziert mit 6-stelligem TOTP-Code.
 * Erreichbar nach Login fuer User die MFA noch nicht eingerichtet haben.
 */
export function MfaSetupPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const { setupTotp, verifyTotpSetup, signOut, refresh } = useAuth();

  const [qrCodeUri, setQrCodeUri] = useState<string | null>(null);
  const [secretKey, setSecretKey] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [keyCopied, setKeyCopied] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // TOTP-Setup initialisieren
  useEffect(() => {
    let cancelled = false;

    async function init() {
      try {
        const result = await setupTotp();
        if (!cancelled) {
          setQrCodeUri(result.qrCodeUri);
          setSecretKey(result.secretKey);
          setIsLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(t('mfaSetup.error.initFailed'));
          setIsLoading(false);
        }
      }
    }

    init();
    return () => { cancelled = true; };
    // Mount-once TOTP setup: deliberately NOT depending on `t` — a language switch must not
    // re-run setupTotp() and regenerate the QR/secret. The init-error message is resolved in
    // the language active at mount, which is acceptable for this rare failure path.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setupTotp]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (code.length !== 6) {
      setError(t('mfaSetup.error.codeRequired'));
      return;
    }

    setIsSubmitting(true);

    try {
      // 1. TOTP verifizieren + als preferred setzen
      await verifyTotpSetup(code);

      // 2. BFF-Call: mfaSetupComplete = true (mit Server-Verifikation)
      await confirmMfaSetup();

      // 3. Token-Refresh erzwingen (damit mfaSetupComplete im ID-Token steht)
      await fetchAuthSession({ forceRefresh: true });

      // 4. Auth-State neu laden
      await refresh();

      toast.success(t('mfaSetup.toast.activated'));
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof Error && err.name === 'EnableSoftwareTokenMFAException') {
        setError(t('mfaSetup.error.invalidCode'));
      } else if (err instanceof Error && err.message?.includes('TOTP nicht aktiv')) {
        setError(t('mfaSetup.error.verifyFailed'));
      } else {
        setError(t('mfaSetup.error.invalidCode'));
      }
      setCode('');
      inputRef.current?.focus();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCodeChange = (value: string) => {
    const cleaned = value.replace(/\D/g, '').slice(0, 6);
    setCode(cleaned);
  };

  const handleCancel = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  const handleCopyKey = async () => {
    if (secretKey) {
      try {
        await navigator.clipboard.writeText(secretKey);
        setKeyCopied(true);
        setTimeout(() => setKeyCopied(false), 2000);
      } catch {
        // Fallback: User muss manuell kopieren
      }
    }
  };

  // Key in 4er-Gruppen formatieren
  const formattedKey = secretKey
    ? secretKey.replace(/(.{4})/g, '$1 ').trim()
    : '';

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <Card className="w-full max-w-md">
        <CardContent className="pt-8 pb-6">
          {/* Brand */}
          <div className="mb-6 flex flex-col items-center gap-2">
            <img
              src={internetqLogo}
              alt="InternetQ"
              className="h-10 w-auto dark:brightness-0 dark:invert"
            />
            <span className="text-xs font-medium text-muted-foreground">Admin Panel</span>
          </div>

          <h2 className="mb-2 text-center text-lg font-semibold">
            {t('mfaSetup.title')}
          </h2>

          <p className="mb-6 text-center text-sm text-muted-foreground">
            {t('mfaSetup.intro')}
          </p>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Spinner size="lg" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* QR-Code */}
              {qrCodeUri && (
                <div className="flex justify-center">
                  <div className="rounded-lg border bg-white p-4">
                    <QRCodeSVG value={qrCodeUri} size={200} />
                  </div>
                </div>
              )}

              {/* Manueller Key */}
              {secretKey && (
                <div className="rounded-md border bg-muted/50 p-3">
                  <p className="mb-1 text-xs font-medium text-muted-foreground">
                    {t('mfaSetup.manualKeyLabel')}
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 break-all text-xs font-mono">
                      {formattedKey}
                    </code>
                    <button
                      type="button"
                      onClick={handleCopyKey}
                      className="shrink-0 text-xs text-muted-foreground hover:text-foreground"
                    >
                      {keyCopied ? t('mfaSetup.copied') : t('mfaSetup.copy')}
                    </button>
                  </div>
                </div>
              )}

              {/* Code-Eingabe */}
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="rounded-md bg-destructive/10 p-3 text-center text-sm text-destructive">
                    {error}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="setup-code">{t('mfaSetup.codeLabel')}</Label>
                  <Input
                    ref={inputRef}
                    id="setup-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="000000"
                    maxLength={6}
                    value={code}
                    onChange={(e) => handleCodeChange(e.target.value)}
                    disabled={isSubmitting}
                    className="text-center text-2xl tracking-[0.5em] font-mono"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={isSubmitting || code.length !== 6}
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <Spinner size="sm" />
                      {t('mfaSetup.submitting')}
                    </span>
                  ) : (
                    t('mfaSetup.submit')
                  )}
                </Button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="text-sm text-muted-foreground hover:text-foreground hover:underline"
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              </form>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
